import React, { useState, useEffect } from "react";
import { get } from "lodash";
import { IconX, IconArrowUpRight } from "@tabler/icons-react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import Button from "../../../../components/ui/Button";
import NewsletterForm from "./NewsletterForm";
import newsletterIllustration from "../../../../assets/images/newsletter-modal.png";

const COUNTRY_NAMES: Record<string, string> = {
  sv: "El Salvador",
  gt: "Guatemala",
  pa: "Panamá",
};

interface Props {
  country: string;
  open: boolean;
}

const NewsletterModal: React.FC<Props> = ({ country, open }) => {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [mounted, setMounted] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Abre el modal cuando open pasa a true (una sola vez)
  useEffect(() => {
    if (!open || visible) return;
    setVisible(true);
    requestAnimationFrame(() => setMounted(true));
  }, [open]);

  const handleClose = () => {
    setMounted(false);
    setStep(1);
    setTimeout(() => setVisible(false), 300);
  };

  const countryName = get(COUNTRY_NAMES, country, country);

  // Contenido compartido entre mobile y web
  const content = (
    <>
      {/* Botón cerrar */}
      <button onClick={handleClose} className="hidden md:inline absolute top-4 right-4 z-10 text-white/80 hover:text-white transition-colors">
        <IconX size={22} />
      </button>

      {/* Contenedor de steps con slide horizontal */}
      <div className="flex h-[80dvh] md:h-full transition-transform duration-500 ease-in-out" style={{ transform: step === 2 ? "translateX(-50%)" : "translateX(0)", width: "200%" }}>
        {/* Step 1 — Promo */}
        <div className="w-1/2 flex-shrink-0 h-full">
          <div className="bg-green600 p-4 md:p-5 flex flex-col items-center justify-between text-center h-full overflow-y-auto md:max-h-[calc(100dvh-2rem)]">
            <p className="text-base font-medium text-white/70 py-2 md:my-6">{countryName} en números</p>

            <img src={newsletterIllustration} alt="Reporte inmobiliario" className="w-44 md:w-72 h-auto mb-2 md:mb-6" />

            <h2 className="text-2xl md:text-[28px] font-semibold text-white leading-[1.1] px-2">
              Recibe el Reporte del Mercado Inmobiliario de {countryName}
            </h2>
            <p className="text-sm text-white/90 mb-2 md:mb-8 px-4">
              Análisis mensual de precios, demanda y oportunidades por zona.
            </p>

            <Button
              onClick={() => setStep(2)}
              variant="white"
              size="base"
              className="!w-full !rounded-xl !h-[42px] !text-[14px] !font-semibold leading-none !tracking-tight"
              endIcon={<IconArrowUpRight size={18} />}
            >
              Recibir reporte mensual
            </Button>
          </div>
        </div>

        {/* Step 2 — Formulario */}
        <div className="w-1/2 flex-shrink-0 h-full">
          <NewsletterForm country={country} countryName={countryName} onClose={handleClose} />
        </div>
      </div>
    </>
  );

  // Mobile: SwipeableDrawer desde abajo con 80dvh
  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={visible && mounted}
        onClose={handleClose}
        onOpen={() => { }}
        disableSwipeToOpen
        swipeAreaWidth={0}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          className: "dynamic",
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            height: "80dvh",
            overflowX: "hidden",
            overflowY: "auto",
            backgroundColor: "#00B66B",
          },
        }}
      >
        {/* Drag handle */}
        <div className="relative overflow-y-hidden md:overflow-y-scroll overflow-x-hidden h-[80dvh] flex flex-col">
          <div className="flex justify-center pt-2 pb-1 bg-green600 absolute top-1 z-[5] left-1/2 -translate-x-1/2">
            <div className="w-10 h-1 rounded-full bg-white/30" />
          </div>
          <div className="flex-1">
            {content}
          </div>
        </div>
      </SwipeableDrawer>
    );
  }

  // Web: modal centrado con backdrop
  if (!visible) return null;

  return (
    <div
      className={`dynamic fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${mounted ? "opacity-100" : "opacity-0"}`}
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Card */}
      <div
        className={`relative w-full max-w-[420px] max-h-[calc(100dvh-2rem)] rounded-2xl overflow-hidden transition-all duration-500 ${mounted ? "scale-100" : "scale-90"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {content}
      </div>
    </div>
  );
};

export default NewsletterModal;
